<script setup lang="ts">
import {useQuasar} from "quasar";
import {useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {useAiSummariesStore, useDashboardStore} from "@/store";
import {computed, type ComputedRef, onMounted} from "vue";
import {type EocSummariesGroupedByMonth, type EocSummariesItem, getEocSummaries} from "@/api/endpoints/statisticsWeb.ts";
import {custom_moment} from '@/plugins/moment-cultured';
import {handleApiRequest} from "@/api/handleApiRequest.ts";
import ThePatientSummaryCard from "@/components/ThePatientSummaryCard.vue";

const props = defineProps({
  eocId: {
    type: Number,
    required: true,
  }
});

const $q = useQuasar(),
  router = useRouter(),
  {t} = useI18n();

const aiSummariesStore = useAiSummariesStore(),
  dashboardStore = useDashboardStore();

const hasAiSums: ComputedRef<boolean> = computed(() => dashboardStore.patientConfig?.features.isAIPatientSummariesEnabled ?? false),
  isLdg: ComputedRef<boolean> = computed(() => sumsGrpdByMon.value.length === 0);

const sumsGrpdByMon: ComputedRef<EocSummariesGroupedByMonth[]> = computed(() => {
  return Object.entries(
    aiSummariesStore.summaries.reduce((acc, sum) => {
      const mon = custom_moment(sum.deliveryDate).format('MMMM');

      (acc[mon] ??= []).push(sum);

      return acc;
    }, {} as Record<string, EocSummariesItem[]>)
  ).map(([month, summaries]) => ({ month, summaries }));
});

function onBackClick() {
  router.push({ name: 'Dashboard.Home', params: { reload: 'true' } });
}

onMounted(async () => {
  if (props.eocId && hasAiSums.value) {
    const eocSumsResp = await handleApiRequest(() => getEocSummaries(props.eocId), null, $q);

    if (eocSumsResp) aiSummariesStore.setAiSummariesStore(eocSumsResp.result);
  } else {
    router.replace({ name: 'Dashboard.Home', params: { reload: 'true' } });
  }
});
</script>

<template>
  <q-page padding>
    <zbm-multi-view-layout :box-padding="$zb.enums.BoxPaddingEnum.p30"
                           :loading="isLdg"
                           :onBackClick="onBackClick">

      <!-- Heading -->
      <template #heading>{{ t('all_updates_title') }}</template>

      <!-- View Unique Content -->
      <template #default>
        <zbm-skeleton-loader height="300px"
                             :loading="isLdg"
                             width="100%">
        
          <!-- Month Group -->
          <section v-for="(grp, i) in sumsGrpdByMon"
                   :key="i">

            <!-- Month -->
            <p class="month">{{ grp.month }}</p>
            
            <!-- Summary Card(s) -->
            <ThePatientSummaryCard v-for="(sum, i) in grp.summaries"
                                   :delivery-date="sum.deliveryDate"
                                   :eoc-id="eocId"
                                   :index="i"
                                   :sum-id="sum.id"
                                   :title="sum.title"
                                   :viewed-date="sum.viewedDate" />
          </section>
        </zbm-skeleton-loader>
      </template>
    </zbm-multi-view-layout>
  </q-page>
</template>

<style scoped lang="scss">
@use 'sass:map';

// Month Group
section {

  &:not(:last-child) {
    margin-block-end: 24px;
  }
}

// Month
.month { // p
  font-weight: map.get($text-weights, semibold);
  line-height: 20px;
  color: map.get($co-brand-clrs, content-txt-subtitle);
  margin-block-end: 8px;
}

// Summary Card(s)
.pat-sum-card {
  width: 100%;
  background-size: contain;
  
  &:not(:last-child) {
    margin-block-end: 8px;
  }
}
</style>